import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

function InstructorCalendarCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');

        if (error) {
          setError(`OAuth error: ${error}`);
          setStatus('error');
          setTimeout(() => navigate('/dashboard'), 3000);
          return;
        }

        if (!code || !state) {
          setError('Missing authorization code or state');
          setStatus('error');
          setTimeout(() => navigate('/dashboard'), 3000);
          return;
        }

        // Parse state to get instructor_id
        let instructorId;
        try {
          const stateData = JSON.parse(state);
          instructorId = stateData.instructor_id;
        } catch (e) {
          setError('Invalid state parameter');
          setStatus('error');
          setTimeout(() => navigate('/dashboard'), 3000);
          return;
        }

        // Send the authorization code to the server
        const response = await fetch('https://klorah-fast-server.onrender.com/api/sessions/calendar/instructor-callback', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code: code,
            state: state,
            instructor_id: instructorId
          })
        });

        if (response.ok) {
          setStatus('success');
          // Redirect back to dashboard after a short delay
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
        } else {
          const errorData = await response.json();
          const errorMessage = errorData.detail || 'Failed to complete calendar connection';
          
          if (errorMessage.includes('Scope has changed')) {
            setError('Failed to connect Google Calendar: Scope has changed from "https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/calendar.events" to "https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/gmail.modify https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/gmail.readonly openid".\nRedirecting to dashboard...');
          } else {
            setError(errorMessage);
          }
          
          setStatus('error');
          setTimeout(() => navigate('/dashboard'), 3000);
        }
      } catch (err) {
        console.error('Calendar callback error:', err);
        setError('Failed to process calendar callback');
        setStatus('error');
        setTimeout(() => navigate('/dashboard'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, currentUser]);

  if (status === 'processing') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-black mb-4">Connecting Google Calendar...</div>
          <div className="text-gray-600">Please wait while we complete the setup.</div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-black mb-4">✅ Google Calendar Connected!</div>
          <div className="text-gray-600">Redirecting to dashboard...</div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-black mb-4">❌ Connection Failed</div>
          <div className="text-gray-600 mb-4">{error || 'There was an error connecting Google Calendar.'}</div>
          <div className="text-gray-600">Redirecting to dashboard...</div>
        </div>
      </div>
    );
  }

  return null;
}

export default InstructorCalendarCallback; 